import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { SaveStatus } from "../../shared/utility/save-status";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../ui-kit/typography/H5/H5";
import KuikaText from "../ui-kit/typography/Text/Text";
import "./folder-modal.scss";

export interface IFolderModalState {
  /** Where CREATE / UPDATE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

/** What the folder form submits. */
export interface IFolderFormValues {
  name: string;
}

export interface IFolderModalProps {
  showModal?: boolean;
  closeModal?: () => void;
  showEditModal?: boolean;
  closeEditModal?: () => void;
  editingFolderId?: string;
  editingFolderName?: string;
  /** Resolves `false` when the caller turned the name down (it says why itself); the dialog stays open. */
  onSubmit: (data: IFolderFormValues) => Promise<boolean>;
  checkFolderNameExists?: (name: string) => boolean;
  folderType?: "datasource" | "screen" | "diagram" | "workflow" | "role";
}

class FolderModal extends React.Component<IFolderModalProps, IFolderModalState> {
  constructor(props: IFolderModalProps) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  /**
   * The commit's order: the ✓ goes up and is held, and only then does the dialog close — the new or
   * renamed folder is then on screen as its node in the tree behind it.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  private formRef = React.createRef<FormInstance>();

  private inputRef = React.createRef<HTMLInputElement>();

  componentDidMount() {
    if (this.props.showModal || this.props.showEditModal) {
      this.inputRef.current?.focus();
    }
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(prevProps: IFolderModalProps) {
    const showModal = this.props.showModal || this.props.showEditModal;
    const prevShowModal = prevProps.showModal || prevProps.showEditModal;

    if (!prevShowModal && showModal) {
      setTimeout(() => {
        if (this.inputRef.current) {
          this.inputRef.current.focus();
        }
      }, 100);
    }
  }

  onSubmit = async (data: IFolderFormValues): Promise<void> => {
    // Not while a commit runs, and not during its ✓: a second Enter would create the folder twice.
    if (!this.saveStatus.canStart) {
      return;
    }

    const folderNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isFolderNameValid = folderNameRules.test(data.name);
    if (!isFolderNameValid) {
      KMainFunctions.displayWarningNotification("Folder Name is invalid");
      return;
    }

    if (this.props.checkFolderNameExists && this.props.checkFolderNameExists(data.name)) {
      KMainFunctions.displayWarningNotification("This folder name already exists.");
      return;
    }

    this.saveStatus.start();
    try {
      const success: boolean = await this.props.onSubmit(data);

      if (!success) {
        this.saveStatus.reset();
        return;
      }

      this.saveStatus.succeed(this.closeModal);
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  closeModal = () => {
    if (this.props.showEditModal && this.props.closeEditModal) {
      this.props.closeEditModal();
    } else if (this.props.closeModal) {
      this.props.closeModal();
    }
  };

  getFolderTypeText = () => {
    switch (this.props.folderType) {
      case "datasource":
        return "Datasource";
      case "screen":
        return "Screen";
      case "diagram":
        return "Data Diagram";
      case "workflow":
        return "Workflow";
      default:
        return "";
    }
  };

  render() {
    const isEditMode = this.props.showEditModal;
    const showModal = this.props.showModal || this.props.showEditModal;
    const header = isEditMode ? "Rename Folder" : "New Folder";
    const folderTypeText = this.getFolderTypeText();

    const footer = (
      <KModalFooter
        onCancel={this.closeModal}
        cancelTestId="cancelButton"
        cancelOnboardingId="common_folder-modal_cancel"
        submitLabel={isEditMode ? "Update" : "Create"}
        submitTestId="updateCreateButton"
        submitOnboardingId="common_folder-modal_submit"
        onSubmit={() => {
          this.formRef.current?.submit();
        }}
        status={this.state.saveStatus}
      />
    );

    return (
      <KModal
        showModal={showModal}
        closeModal={this.closeModal}
        header={header}
        footer={footer}
        width={460}
        centered
        isLoading={this.state.saveStatus === "loading"}
        wrapTestId="folderModalWrap"
      >
        <div className="kuika-folder-modal">
          <div className="kuika-folder-modal__header">
            <KuikaModalIconWrapper iconName="folder" iconType="sidebar-icons" intent={isEditMode ? "edit" : "create"} />
            <div className="kuika-folder-modal__title">
              <KuikaH5>{header}</KuikaH5>
              {folderTypeText && (
                <KuikaText className="kuika-folder-modal__subtitle">
                  {isEditMode ? "Rename" : "Create a new"} {folderTypeText.toLowerCase()} folder
                </KuikaText>
              )}
            </div>
          </div>

          <div className="kuika-folder-modal__content">
            <Form
              id="folder-form"
              ref={this.formRef}
              name="basic"
              onFinish={this.onSubmit}
              onFinishFailed={this.onFinishFailed}
              initialValues={{ name: this.props.editingFolderName }}
            >
              <div className="kuika-folder-modal__form-item">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a folder name!" },
                    { pattern: new RegExp("^[a-zA-Z0-9 ]+$"), message: "Field accepts only english letters!" }
                  ]}
                >
                  <KuikaInput
                    data-testid="folderNameInput"
                    data-onboarding-id="common_folder-modal_name-input"
                    ref={this.inputRef}
                    placeholder="Enter folder name"
                    size="md"
                  />
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </KModal>
    );
  }
}

export default FolderModal;
